import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  HandRaisedIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  HomeIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const CommunityGuidelines: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full">
              <ShieldCheckIcon className="h-12 w-12 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Community Guidelines
          </h1>
          <p className="text-xl text-center text-gray-600">
            Creating a safe, respectful, and trustworthy rental community
          </p>
          <p className="text-center text-gray-500 mt-4">
            Last updated: November 6, 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Welcome to RentSmart.ie
          </h2>
          <p className="text-gray-700 mb-4">
            RentSmart.ie is committed to fostering a safe, respectful, and
            trustworthy community for landlords and renters across Ireland.
            These Community Guidelines outline the standards of behavior we
            expect from all users to ensure a positive experience for everyone.
          </p>
          <p className="text-gray-700">
            By using RentSmart.ie, you agree to follow these guidelines.
            Violation of these guidelines may result in warnings, temporary
            suspension, or permanent removal from our platform.
          </p>
        </div>

        {/* Core Values */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <UserGroupIcon className="h-7 w-7 text-blue-600" />
            Our Core Values
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Respect</h3>
                <p className="text-gray-600 text-sm">
                  Treat all community members with dignity and courtesy
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Honesty</h3>
                <p className="text-gray-600 text-sm">
                  Provide accurate and truthful information in all interactions
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Safety</h3>
                <p className="text-gray-600 text-sm">
                  Prioritize the safety and security of all users
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Fairness</h3>
                <p className="text-gray-600 text-sm">
                  Conduct all transactions and communications fairly
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Do's and Don'ts */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Do's and Don'ts
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Do's */}
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-4 flex items-center gap-2">
                <CheckCircleIcon className="h-6 w-6" />
                Please Do
              </h3>
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>
                    Be respectful and professional in all communications
                  </span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Provide accurate property information and photos</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Respond to inquiries in a timely manner</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Honor commitments and agreements</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Report suspicious activity or violations</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Protect your personal and financial information</span>
                </li>
              </ul>
            </div>

            {/* Don'ts */}
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
                <XCircleIcon className="h-6 w-6" />
                Please Don't
              </h3>
              <ul className="space-y-3">
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>
                    Use offensive, discriminatory, or abusive language
                  </span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>Post false or misleading information</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>Engage in fraudulent or deceptive practices</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>Harass, threaten, or intimidate other users</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>Share others' private information without consent</span>
                </li>
                <li className="flex gap-3 text-gray-700">
                  <span className="text-red-600 font-bold">✗</span>
                  <span>Spam or send unsolicited messages</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Prohibited Activities */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <ExclamationTriangleIcon className="h-7 w-7 text-red-600" />
            Prohibited Activities
          </h2>
          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                Discrimination
              </h3>
              <p className="text-gray-700 text-sm">
                Discrimination based on race, religion, national origin, gender,
                age, marital status, sexual orientation, disability, or any
                other protected characteristic is strictly prohibited.
              </p>
            </div>
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                Fraud and Scams
              </h3>
              <p className="text-gray-700 text-sm">
                Any fraudulent activity, including fake listings, identity
                theft, payment scams, or attempts to deceive other users is
                prohibited.
              </p>
            </div>
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                Harassment and Abuse
              </h3>
              <p className="text-gray-700 text-sm">
                Harassment, bullying, threats, hate speech, or any form of
                abusive behavior towards other users is not tolerated.
              </p>
            </div>
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                Illegal Activities
              </h3>
              <p className="text-gray-700 text-sm">
                Using the platform for illegal purposes, including money
                laundering, tax evasion, or any violation of Irish law is
                strictly prohibited.
              </p>
            </div>
            <div className="border-l-4 border-red-500 pl-4 py-2">
              <h3 className="font-semibold text-gray-900 mb-2">
                Spam and Solicitation
              </h3>
              <p className="text-gray-700 text-sm">
                Sending unsolicited messages, promotional content, or using the
                platform for purposes other than rental housing is not allowed.
              </p>
            </div>
          </div>
        </div>

        {/* Property Listings */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <HomeIcon className="h-7 w-7 text-blue-600" />
            Property Listing Guidelines
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Accurate Information
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Provide truthful and complete property descriptions</li>
                <li>Use current, unedited photos of the actual property</li>
                <li>Clearly state all fees, deposits, and rental terms</li>
                <li>Disclose any property issues or limitations</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Legal Compliance
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>
                  Ensure property meets all safety and habitability standards
                </li>
                <li>Comply with local zoning and rental regulations</li>
                <li>Have proper authorization to list the property</li>
                <li>Follow fair housing laws and regulations</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Quality Standards
              </h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                <li>Use clear, well-lit photos</li>
                <li>Write detailed, informative descriptions</li>
                <li>Keep listing information up-to-date</li>
                <li>Remove listings promptly when property is rented</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Communication */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="h-7 w-7 text-blue-600" />
            Communication Standards
          </h2>
          <div className="space-y-4 text-gray-700">
            <p>
              <strong>Be Professional:</strong> Maintain a professional tone in
              all messages, even if disagreements arise.
            </p>
            <p>
              <strong>Be Responsive:</strong> Reply to inquiries within 24-48
              hours whenever possible. If you need more time, send a brief
              acknowledgment.
            </p>
            <p>
              <strong>Be Clear:</strong> Communicate expectations, requirements,
              and conditions clearly to avoid misunderstandings.
            </p>
            <p>
              <strong>Be Respectful:</strong> Respect others' time and
              circumstances. Don't ghost or ignore legitimate inquiries.
            </p>
            <p>
              <strong>Keep It On Platform:</strong> For your safety, conduct all
              communications through RentSmart.ie messaging until you've
              verified the other party.
            </p>
          </div>
        </div>

        {/* Safety Tips */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <HandRaisedIcon className="h-7 w-7 text-blue-600" />
            Safety Tips
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">For Renters</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• View properties in person before committing</li>
                <li>• Never send money before viewing a property</li>
                <li>• Verify the landlord's identity and ownership</li>
                <li>• Get all agreements in writing</li>
                <li>
                  • Trust your instincts - if it seems too good to be true, it
                  probably is
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">
                For Landlords
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Screen tenants thoroughly but fairly</li>
                <li>• Meet prospective tenants in safe locations</li>
                <li>• Verify tenant identity and references</li>
                <li>• Document property condition before move-in</li>
                <li>• Use secure payment methods</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Reporting Violations */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Reporting Violations
          </h2>
          <p className="text-gray-700 mb-4">
            If you encounter behavior that violates these Community Guidelines,
            please report it immediately. We take all reports seriously and will
            investigate promptly.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-blue-900 mb-2">How to Report</h3>
            <ul className="space-y-2 text-blue-800 text-sm">
              <li>• Use the "Report" button on listings or messages</li>
              <li>
                • Contact us at{' '}
                <a href="mailto:support@rentsmart.ie" className="underline">
                  support@rentsmart.ie
                </a>
              </li>
              <li>
                • Visit our{' '}
                <Link to="/report-listing" className="underline">
                  Report Listing
                </Link>{' '}
                page
              </li>
              <li>• Provide as much detail and evidence as possible</li>
            </ul>
          </div>
          <p className="text-gray-600 text-sm">
            All reports are confidential. We may request additional information
            to investigate the issue.
          </p>
        </div>

        {/* Consequences */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Consequences of Violations
          </h2>
          <p className="text-gray-700 mb-4">
            Violations of these Community Guidelines may result in the following
            actions:
          </p>
          <div className="space-y-3">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-700 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Warning</h3>
                <p className="text-gray-600 text-sm">
                  First-time or minor violations may receive a warning
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-700 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Content Removal</h3>
                <p className="text-gray-600 text-sm">
                  Violating content may be removed from the platform
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-700 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Temporary Suspension
                </h3>
                <p className="text-gray-600 text-sm">
                  Account may be temporarily suspended for repeated or serious
                  violations
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-red-200 rounded-full flex items-center justify-center">
                <span className="text-red-900 font-bold">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Permanent Ban</h3>
                <p className="text-gray-600 text-sm">
                  Severe or repeated violations may result in permanent account
                  termination
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Updates to Guidelines */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Updates to These Guidelines
          </h2>
          <p className="text-gray-700 mb-4">
            RentSmart.ie reserves the right to update these Community Guidelines
            at any time. We will notify users of significant changes via email
            or platform notifications. Continued use of the platform after
            changes constitutes acceptance of the updated guidelines.
          </p>
        </div>

        {/* Contact */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Questions?
          </h3>
          <p className="text-gray-700 mb-4">
            If you have questions about these Community Guidelines, please
            contact us:
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@rentsmart.ie"
              className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Email Support
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Contact Form
            </Link>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 text-center text-gray-600">
          <p className="mb-2">Related Documents:</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/terms-of-service"
              className="text-blue-600 hover:underline"
            >
              Terms of Service
            </Link>
            <Link
              to="/privacy-policy"
              className="text-blue-600 hover:underline"
            >
              Privacy Policy
            </Link>
            <Link to="/safety" className="text-blue-600 hover:underline">
              Safety Center
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;
